import React, { useContext, useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';

// Module imports.
import axios from 'axios';
import OTPInput from 'otp-input-react';
import codes from 'country-calling-code';

// Components import.
import { AuthContext, Animate, Row, Card, ViewContext, useCan } from 'components/lib';
import { AivagamButton } from 'components/global/AivagamButton/AivagamButton';

export function MobileVerify(props) {

	const context = useContext(AuthContext);
	const viewContext = useContext(ViewContext);

	const [phoneInput, setPhoneInput] = useState(false);
	const [OTP, setOTP] = useState('');
	const [countryCode, setCountryCode] = useState('91');
	const [phoneNumber, setPhoneNumber] = useState('');
	const [loading, setLoading] = useState(false);
	const [isChecked, setChecked] = useState(true);
	const [enableVerify, setEnableVerify] = useState(false);

	const initialEditTime = 10;
	const initialResendTime = 15;
	const expirationTime = 15 * 60 * 1000; // 15 minutes

	const [resendTime, setResendTime] = useState(15);
	const [editTimer, setEditTimer] = useState(initialEditTime);
	const [resendTimer, setResendTimer] = useState(resendTime);
	const [editDisabled, setEditDisabled] = useState(true);
	const [resendDisabled, setResendDisabled] = useState(true);
	const [resendAttempts, setResendAttempts] = useState(1);
	const [expired, setExpired] = useState(false);
	const [timerStarted, setTimerStarted] = useState(false);

	const canNavigate = useCan('mobile.verify.navigate');

	let marketing = isChecked ? '1' : '0';

	// Css styles
	const paraContainer = {
		display: 'flex',
		justifyContent: 'center',
		textAlign: 'center',
		color: 'grey',
	};

	const optInput = {
		border: '1.5px solid #E2E8F0',
		marginRight: '5px',
		height: '3.5rem',
		width: '3.5rem',
		borderRadius: '10px',
		textAlign: 'center',
		color: 'black',
	};

	const numInput = {
		border: '1.5px solid #E2E8F0',
		marginRight: '5px',
		height: '3rem',
		width: '70%',
		borderRadius: '8px',
		color: 'black',
		paddingLeft: '10px',
	};
	const checkbox = {
		marginRight: '8px',
		cursor: 'pointer',
		width: '16px',
		height: '16px',
	}

	const label = {
		display: 'flex',
		alignItems: 'center',
		fontSize: ' 16px',
		cursor: 'pointer',
	}
	const buttonContainer = {
		width: "100%",
		height: "3rem",
		fontSize: "1.2em",
		padding: "0.5em",
		marginBottom: "0.6em",
		fontWeight: "600"
	}

	// UseEffects
	useEffect(() => {
		if (!timerStarted) return;

		const editInterval = setInterval(() => {
			if (editTimer > 0) {
				setEditTimer(editTimer - 1);
			} else {
				setEditDisabled(false);
				clearInterval(editInterval);
			}
		}, 1000);

		return () => clearInterval(editInterval);
	}, [editTimer, timerStarted]);

	useEffect(() => {
		if (!timerStarted || !resendDisabled) return;

		const resendInterval = setInterval(() => {
			if (resendTimer > 0) {
				setResendTimer(resendTimer - 1);
			} else {
				setResendDisabled(false);
			}
		}, 1000);

		return () => clearInterval(resendInterval);
	}, [resendTimer, resendDisabled, timerStarted]);

	useEffect(() => {
		if (!timerStarted) return;

		const expirationTimeout = setTimeout(() => {
			setExpired(true);
			setResendDisabled(true);
		}, expirationTime);

		return () => clearTimeout(expirationTimeout);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [timerStarted]);

	useEffect(() => {
		if (OTP.length === 6) {
			setEnableVerify(true);
		} else {
			setEnableVerify(false);
		}
	}, [OTP]);


	// Functions.	
	const handleCheckboxChange = () => {
		setChecked(!isChecked);
	};

	const validatePhone = (phoneNumber) => {
		const mobileNumberRegex = /^\s*(\d\s*){7,15}\s*$/;
		const isValid = mobileNumberRegex.test(phoneNumber);
		return isValid;
	};

	const handleResend = () => {

		const options = {
			method: 'POST',
			url: '/api/account/OTP/resend',
			data: {
				accountId: context.user?.account_id,
				to: countryCode + phoneNumber,
				phoneNumber: phoneNumber,
				countryCode: countryCode
			}
		};

		axios
			.request(options)
			.then(function (response) {
				if (response.data.send === true) {
					setResendAttempts(resendAttempts + 1);
					setResendTime(resendTime * 2);
					setResendTimer(resendTime * 2);
					setResendDisabled(true);

					viewContext.notification.show(response.data.message, 'success');
				}
				else {
					viewContext.notification.show(response.data.message, 'error');
				}
			})
			.catch((err) => {
				if (err.response?.data?.message) {
					viewContext.notification.show(err.response.data?.message, 'error');
				} else {
					viewContext.notification.show(err.response.data, 'error');
				}
			});

	};

	const continueClick = (e) => {

		if (loading) return;
		setLoading(true);
		const validPhone = validatePhone(phoneNumber);
		if (!validPhone) {
			viewContext.notification.show(props.t('auth.signup.mobileVerification.notification.invalid'), 'error');
			setLoading(false);
			return;
		}

		const options = {
			method: 'POST',
			url: '/api/account/mobile',
			data: {
				account_id: context.user?.account_id,
				phone: phoneNumber,
				phoneNumber: countryCode + phoneNumber,
			},
		};

		axios
			.request(options)
			.then(function (response) {
				if (response.data.send === true) {
					setPhoneInput(true);

					setTimerStarted(false); // Stop existing timers
					setEditTimer(initialEditTime);
					setResendTimer(initialResendTime);
					setEditDisabled(true);
					setResendDisabled(true);
					setExpired(false);
					setResendAttempts(1);
					setTimerStarted(true); // Start new timer

					viewContext.notification.show(response.data.message, 'success');
				} else {
					viewContext.notification.show(response.data.message, 'error');
				}
			})
			.catch((err) => {
				if (err.response?.data?.message) {
					viewContext.notification.show(err.response?.data?.message, 'error');
				} else {
					viewContext.notification.show(err.response.data, 'error');
				}
			});
		setLoading(false);
	};

	const verifyClick = () => {

		if (loading) return;
		setLoading(true);

		const options = {
			method: 'POST',
			url: '/api/account/mobilecheck',
			data: {
				account_id: context.user?.account_id,
				to: countryCode + phoneNumber,
				phoneNumber: phoneNumber,
				countryCode: countryCode,
				otp: OTP,
				marketing: marketing
			},
		};

		axios
			.request(options)
			.then(function (response) {
				if (response.data?.verified === true) {
					viewContext.notification.show(response.data.message, 'success');
					context.update({ onboarded: response.data.onboarded });
					if (!context.user?.verified) {
						window.location = '/signup/verify';
					} else {
						if (canNavigate) {
							window.location = '/signup/logo';
						} else {
							window.location = '/navigate';
						}
					}
				} else {
					viewContext.notification.show(response.data.message, 'error');
				}
			})
			.catch((err) => {
				if (err.response?.data?.message) {
					viewContext.notification.show(err.response.data?.message, 'error');
				} else {
					viewContext.notification.show(err.response.data, 'error');
				}
			});
		setLoading(false);
	};

	return (
		context.user?.verified ?
			<Animate>
				{!phoneInput && (
					<Row title={props.t('auth.signup.mobileVerification.mobileNumber.title')}>
						<div style={paraContainer}>
							<p>{props.t('auth.signup.mobileVerification.mobileNumber.info')}</p>
						</div>
						<Card loading={false} restrictWidth center>
							{/* <SocialSignin network={['facebook', 'twitter']} showOr signup /> */}
							<div style={{ display: 'flex' }}>
								<select
									style={{ ...numInput, width: '25%', paddingLeft: '3px' }}
									onChange={(e) => {
										setCountryCode(e.target.value);
									}}
									value={countryCode}
								>
									{codes &&
										codes.map((country, index) => {
											return (
												<option key={country + index} value={country.countryCodes}>
													{country.isoCode3} +{country.countryCodes}
												</option>
											);
										})}
								</select>

								<input
									type="text"
									value={phoneNumber}
									onChange={(e) => {
										setPhoneNumber(e.target.value);
									}}
									style={numInput}
								/>
							</div>
							<div style={{ marginTop: '20px' }}>
								<label style={{ ...label }}>
									<input style={{ ...checkbox }}
										type="checkbox"
										checked={isChecked}
										onChange={handleCheckboxChange}
									/>
									{props.t('auth.signup.mobileVerification.mobileNumber.marketing')}
								</label>
							</div>

							<div style={{ ...paraContainer, marginTop: '20px' }}>
								<AivagamButton
									id='continueButton'
									buttonText={props.t('auth.signup.mobileVerification.mobileNumber.button.continue')}
									onClick={continueClick}
									classStyles={buttonContainer}
									type="confirm"
									loading={loading}
								/>
							</div>
							<button
								style={{ color: '#3B82F6', textDecoration: 'underline', marginTop: '3px' }}
								onClick={() => {
									context.signout();
								}}
							>
								{props.t('auth.signup.mobileVerification.mobileNumber.button.signOut')}
							</button>
						</Card>
					</Row>
				)}
				{phoneInput && (
					<Row title={props.t('auth.signup.mobileVerification.otp.title')}>
						<div style={{ display: 'flex', justifyContent: 'center', gap: '80px' }}>
							<p>{props.t('auth.signup.mobileVerification.otp.info', { phoneNumber: phoneNumber })} </p>{' '}
							<button
								style={{ color: editDisabled ? 'grey' : '#3B82F6' }}
								onClick={() => {
									setPhoneInput(false);
									setLoading(false);
								}}
								disabled={editDisabled}
							>
								{props.t('auth.signup.mobileVerification.otp.button.edit')} {editTimer > 0 && `${editTimer}s`}
							</button>

						</div>
						<Card loading={false} restrictWidth center>
							<div style={{ display: 'flex', justifyContent: 'center' }}>
								<OTPInput
									value={OTP}
									onChange={setOTP}
									autoFocus
									OTPLength={6}
									otpType="number"
									disabled={false}
									inputStyles={optInput}
								/>
							</div>
							<div>
								<p>	</p>
							</div>
							<div style={{ ...paraContainer, marginTop: '20px' }}>

								<AivagamButton
									id='continueButton'
									buttonText={props.t('auth.signup.mobileVerification.otp.button.verify')}
									onClick={verifyClick}
									classStyles={buttonContainer}
									type="confirm"
									disabled={!enableVerify}
									loading={loading}
								/>
							</div>
							<div style={{
								display: "flex",
								flexDirection: "column",
								gap: "0.4em",
								alignItems: "flex-start"
							}}>
								{expired ?
									<>
										<p> {props.t('auth.signup.mobileVerification.otp.new.info')} {
											<button
												style={{ color: '#3B82F6' }}
												onClick={continueClick}
											>
												{props.t('auth.signup.mobileVerification.otp.new.button')}	</button>}.</p>
									</>
									:
									<button
										style={{ color: resendDisabled ? 'grey' : '#3B82F6' }}
										onClick={handleResend}
										disabled={resendDisabled}
									>
										{props.t('auth.signup.mobileVerification.otp.button.resend')} {resendTimer > 0 && `${resendTimer}s`}
									</button>
								}
								<button
									style={{ color: '#3B82F6', textDecoration: 'underline', marginTop: '3px' }}
									onClick={() => {
										context.signout();
									}}
								>
									{props.t('auth.signup.mobileVerification.otp.button.signOut')}
								</button>
							</div>
						</Card>
					</Row>
				)
				}
			</Animate > : <Navigate to='/signup/verify' />
	);
}
